import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';

const Jobs = () => {
    const [page, setPage] = useState(0);
    const [filter, setFilter] = useState('all');

    const { data: jobsData, isLoading, refetch } = useQuery(
        ['jobs', page, filter],
        () => axios.get(`/users/jobs?offset=${page * 20}&limit=20`).then(res => res.data),
        {
            refetchInterval: 5000, // Refetch every 5 seconds
            keepPreviousData: true
        }
    );

    const getStatusBadge = (status) => {
        const badges = {
            pending: 'badge-pending',
            processing: 'badge-processing',
            completed: 'badge-completed',
            failed: 'badge-failed'
        };
        return badges[status] || 'badge-pending';
    };

    const getJobIcon = (jobType) => {
        const icons = {
            avatar_video: '🎥',
            streaming_avatar: '🤖',
            voice_clone: '🎤',
            talking_photo: '📸',
            video_translation: '🌐',
            template_video: '📄'
        };
        return icons[jobType] || '📋';
    };

    const getJobTypeName = (jobType) => {
        const names = {
            avatar_video: 'Avatar Video',
            streaming_avatar: 'Streaming Avatar',
            voice_clone: 'Voice Clone',
            talking_photo: 'Talking Photo',
            video_translation: 'Video Translation',
            template_video: 'Template Video'
        };
        return names[jobType] || jobType;
    };

    const filteredJobs = jobsData?.jobs?.filter(job => {
        if (filter === 'all') return true;
        return job.status === filter;
    }) || [];

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Job Management</h1>
                <p className="text-gray-600">
                    Monitor and track your video generation jobs
                </p>
            </div>

            {/* Filter Tabs */}
            <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                    {['all', 'pending', 'processing', 'completed', 'failed'].map((status) => (
                        <button
                            key={status}
                            onClick={() => setFilter(status)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === status
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Jobs List */}
            <div className="bg-white rounded-xl shadow-md border">
                {isLoading ? (
                    <div className="flex justify-center items-center py-12">
                        <LoadingSpinner />
                    </div>
                ) : filteredJobs.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="text-6xl mb-4">📋</div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {filter === 'all' ? 'No jobs found' : `No ${filter} jobs`}
                        </h3>
                        <p className="text-gray-600 mb-6">
                            {filter === 'all'
                                ? 'Start by generating your first video'
                                : `You don't have any ${filter} jobs yet`
                            }
                        </p>
                        {filter === 'all' && (
                            <button
                                onClick={() => window.location.href = '/video-generation'}
                                className="btn btn-primary"
                            >
                                Generate Video
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="divide-y divide-gray-200">
                        {filteredJobs.map((job) => (
                            <div key={job.id} className="p-6">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start space-x-4 flex-1">
                                        <div className="text-3xl">{getJobIcon(job.job_type)}</div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-2 mb-2">
                                                <h3 className="font-medium text-gray-900">
                                                    {getJobTypeName(job.job_type)}
                                                </h3>
                                                <span className={`badge ${getStatusBadge(job.status)}`}>
                                                    {job.status}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                                <div>
                                                    <span className="font-medium">Created:</span>{' '}
                                                    {new Date(job.created_at).toLocaleString()}
                                                </div>

                                                {job.completed_at && (
                                                    <div>
                                                        <span className="font-medium">Completed:</span>{' '}
                                                        {new Date(job.completed_at).toLocaleString()}
                                                    </div>
                                                )}

                                                {job.heygen_job_id && (
                                                    <div>
                                                        <span className="font-medium">HeyGen ID:</span>{' '}
                                                        <code className="text-xs bg-gray-100 px-1 rounded">
                                                            {job.heygen_job_id}
                                                        </code>
                                                    </div>
                                                )}

                                                <div>
                                                    <span className="font-medium">Job ID:</span>{' '}
                                                    <code className="text-xs bg-gray-100 px-1 rounded">
                                                        {job.id}
                                                    </code>
                                                </div>
                                            </div>

                                            {/* Input Preview */}
                                            {job.input_data && (
                                                <div className="mt-3">
                                                    <details className="group">
                                                        <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-500">
                                                            View Input Data
                                                        </summary>
                                                        <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm">
                                                            {job.input_data.text && (
                                                                <div className="mb-2">
                                                                    <span className="font-medium">Text:</span>{' '}
                                                                    <span className="italic">"{job.input_data.text.substring(0, 100)}
                                                                        {job.input_data.text.length > 100 ? '...' : ''}"</span>
                                                                </div>
                                                            )}
                                                            {job.input_data.avatar_id && (
                                                                <div className="mb-1">
                                                                    <span className="font-medium">Avatar:</span> {job.input_data.avatar_id}
                                                                </div>
                                                            )}
                                                            {job.input_data.voice_id && (
                                                                <div>
                                                                    <span className="font-medium">Voice:</span> {job.input_data.voice_id}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </details>
                                                </div>
                                            )}

                                            {/* Error Message */}
                                            {job.error_message && (
                                                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                                    <p className="text-sm text-red-700">
                                                        <span className="font-medium">Error:</span> {job.error_message}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Result Data */}
                                            {job.result_data && job.status === 'completed' && (
                                                <div className="mt-3">
                                                    {job.result_data.video_url && (
                                                        <div className="flex items-center space-x-2">
                                                            <a
                                                                href={job.result_data.video_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-primary btn-sm"
                                                            >
                                                                📥 Download Video
                                                            </a>
                                                            <a
                                                                href={job.result_data.video_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-outline btn-sm"
                                                            >
                                                                👀 Preview
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Status Indicator */}
                                    <div className="flex items-center space-x-2">
                                        {job.status === 'processing' && (
                                            <div className="flex items-center space-x-1 text-blue-600">
                                                <LoadingSpinner size="small" />
                                                <span className="text-sm">Processing...</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Refresh Button */}
            <div className="mt-6 text-center">
                <button
                    onClick={() => refetch()}
                    className="btn btn-outline"
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <LoadingSpinner size="small" className="mr-2" />
                    ) : (
                        '🔄'
                    )}{' '}
                    Refresh Jobs
                </button>
            </div>
        </div>
    );
};

export default Jobs;